import _ from 'lodash';

import {
  CredentialsProps,
  LOGGED_IN,
  LOGGING_IN,
  LOGGED_OUT,
  LOG_IN_FAILED,
  User,
  ILoggedInAction,
  ILoggingInAction,
  ILoggedOutAction,
  ILoggedInFailed,
  AuthenticateActionTypes,
} from '@store/types';
import { setError } from '@store/actions';

import { ErrorStateType } from '@models';

import {
  authenticate as authenticateRequest,
  logout as logoutRequest,
  isAuthenticated as isAuthenticatedRequest,
  csrfToken as csrfTokenRequest,
  IUserDto,
} from '@services/auth';

import { app as routes } from '@configs/routes.json';

import * as localeStorage from '@utils';
import { RequestError, HttpStatusCode, setLanguage } from '@utils';

export const loggedIn = (user: User): ILoggedInAction => ({
  type: LOGGED_IN,
  payload: user,
});

export const loggingIn = (): ILoggingInAction => ({
  type: LOGGING_IN,
});

export const logInFailed = (error: string): ILoggedInFailed => ({
  type: LOG_IN_FAILED,
  payload: error,
});

export const logOut = () => {
  return async () => {
    await logoutRequest();
    localStorage.removeItem('current-theme');
    /**
     * The following line of code causes the page to refresh.
     *
     * As of this writing, we are heavily relying on making requests
     * and setting the application state in the initial load of the
     * application.
     *
     * Therefore, it's not trivial to implement logout functionality without a
     * refresh.
     *
     * - Why are we not calling `dispatch(loggedOut())` here?
     *  + It's called indirectly from `index.tsx` during the initial load if
     *    there's no authenticated user. If we call it here again, we will cause
     *    double refresh on logout.
     *
     * - `setCsrfToken` procedure in `method.ts` is buggy. It doesn't set a new
     *   token after the initial set. The page refresh circumvent this issue at
     *   the moment.
     *
     * - Even if we resolve the previous 2 issues, we still need to set the
     *   application state to a good initial state without old values lurking.
     */
    document.location.pathname = routes.login;
  };
};

export const loggedOut = (): ILoggedOutAction => ({
  type: LOGGED_OUT,
  payload: null,
});

export const setAccountId = (accountId: string): void => {
  localeStorage.setAccountId(accountId);
};

export const didLogIn = async (
  dto: IUserDto,
  dispatch: (action: ILoggedInAction | any) => void
) => {
  // If not a super user assume the first permission (if any) is the account id to use
  const accountId =
    !dto.adaptiveSuperUser &&
    _(dto.permissions)
      .map((q) => q.accountId)
      .first();

  const user: User = {
    firstName: dto.firstName,
    lastName: dto.lastName,
    hasValidTOS: dto.hasValidTOS,
    email: dto.email,
    institution: dto.institution,
    accountId: accountId || localeStorage.getAccountId(dto.email) || undefined,
    adaptiveSuperUser: dto.adaptiveSuperUser,
    permissions: dto.permissions,
  };

  if (dto.hasValidTOS === false) {
    dispatch(setError(ErrorStateType.InvalidTOS));
  }

  dispatch(loggedIn(user));

  if (dto.language) {
    setLanguage(dto.language, true);
  }
};

export const logIn = (content: CredentialsProps): any => {
  return (dispatch: (action: AuthenticateActionTypes) => void) => {
    const { username, password } = content;
    dispatch(loggingIn());

    return authenticateRequest(username, password)
      .then((dto: IUserDto) => {
        didLogIn(dto, dispatch);
      })
      .catch((error: RequestError) => {
        let message = 'login_error_default';
        if (error.status === HttpStatusCode.UNAUTHORIZED) {
          switch (error.code) {
            case ErrorStateType.LockedAccount:
              message = 'login_error_unauthorized_lockedAccount';
              break;
            case ErrorStateType.InvalidUsernamePassword:
              message = 'login_error_unauthorized';
              break;
            default:
              message = 'login_error_default';
              break;
          }
        }

        return dispatch(logInFailed(message));
      });
  };
};

export const isLoggedIn = (): any => {
  return (dispatch: (action: AuthenticateActionTypes) => void) => {
    return isAuthenticatedRequest()
      .then((dto?: IUserDto) => {
        if (!dto) {
          dispatch(loggedOut());
          return;
        }
        didLogIn(dto, dispatch);
      })
      .catch(() => {});
  };
};

export const setCsrfToken = (): Promise<void> => csrfTokenRequest();
